/**
 * SommelAI Advanced Search & Recommendation Engine
 * Phase 2: Core AI Features - NLP-powered search and personalized recommendations
 *
 * This service provides intelligent search capabilities, preference-based recommendations,
 * and natural language processing for conversational beverage discovery.
 */

import { aiServiceIntegration } from "./aiServiceIntegration.js";

class AdvancedRecommendationEngine {
  constructor() {
    this.searchCache = new Map();
    this.recommendationCache = new Map();
    this.preferenceProfiles = new Map();
    this.cacheExpiry = 30 * 60 * 1000; // 30 minutes for searches
    this.profileExpiry = 7 * 24 * 60 * 60 * 1000; // 7 days for profiles
  }

  /**
   * Natural language search interface for beverages
   * @param {string} query - Natural language search query
   * @param {Array} inventory - Available beverage inventory
   * @param {Object} context - Search context (user preferences, occasion, etc.)
   * @returns {Object} Search results with explanations
   */
  async naturalLanguageSearch(query, inventory, context = {}) {
    const cacheKey = JSON.stringify({
      query,
      inventoryHash: this.hashInventory(inventory),
      context,
    });

    // Check cache first
    if (this.searchCache.has(cacheKey)) {
      const cached = this.searchCache.get(cacheKey);
      if (Date.now() - cached.timestamp < this.cacheExpiry) {
        return cached.data;
      }
    }

    try {
      // Parse and understand the search intent
      const searchIntent = await this.parseSearchIntent(query, context);

      // Filter inventory based on understood intent
      const candidates = await this.filterByIntent(inventory, searchIntent);

      // Rank candidates using AI
      const rankedResults = await this.rankSearchResults(
        candidates,
        searchIntent,
        query
      );

      // Generate explanations for top results
      const explainedResults = await this.explainSearchResults(
        rankedResults,
        query,
        searchIntent
      );

      const results = {
        query,
        intent: searchIntent,
        results: explainedResults,
        totalFound: candidates.length,
        searchTime: Date.now(),
        suggestions: await this.generateSearchSuggestions(query, inventory),
      };

      // Cache the results
      this.searchCache.set(cacheKey, {
        data: results,
        timestamp: Date.now(),
      });

      return results;
    } catch (error) {
      console.error("Error in natural language search:", error);
      return this.fallbackSearch(query, inventory);
    }
  }

  /**
   * Generate personalized beverage recommendations
   * @param {Object} preferences - User preferences and history
   * @param {Array} inventory - Available beverage inventory
   * @param {Object} context - Recommendation context
   * @returns {Array} Personalized recommendations with reasoning
   */
  async getPersonalizedRecommendations(preferences, inventory, context = {}) {
    const cacheKey = JSON.stringify({
      preferences: this.hashPreferences(preferences),
      inventoryHash: this.hashInventory(inventory),
      context,
    });

    // Check cache first
    if (this.recommendationCache.has(cacheKey)) {
      const cached = this.recommendationCache.get(cacheKey);
      if (Date.now() - cached.timestamp < this.cacheExpiry) {
        return cached.data;
      }
    }

    try {
      // Build preference profile
      const profile = await this.buildPreferenceProfile(preferences, context);

      // Score all inventory items against profile
      const scoredItems = await this.scoreInventoryAgainstProfile(
        inventory,
        profile
      );

      // Apply diversity and exploration factors
      const diversifiedRecommendations = await this.diversifyRecommendations(
        scoredItems,
        profile
      );

      // Generate detailed explanations
      const explainedRecommendations = await this.explainRecommendations(
        diversifiedRecommendations,
        profile,
        context
      );

      const recommendations = {
        profile,
        recommendations: explainedRecommendations,
        context,
        generatedAt: Date.now(),
        confidence: this.calculateOverallConfidence(explainedRecommendations),
      };

      // Cache the results
      this.recommendationCache.set(cacheKey, {
        data: recommendations,
        timestamp: Date.now(),
      });

      return recommendations;
    } catch (error) {
      console.error("Error generating personalized recommendations:", error);
      return this.fallbackRecommendations(preferences, inventory);
    }
  }

  /**
   * Learn from user interactions to improve recommendations
   * @param {string} userId - User identifier
   * @param {Object} interaction - User interaction data
   * @returns {Object} Updated preference profile
   */
  async learnFromInteraction(userId, interaction) {
    try {
      let profile =
        this.preferenceProfiles.get(userId) || this.createEmptyProfile(userId);

      // Update profile based on interaction type
      switch (interaction.type) {
        case "like":
          profile = await this.incorporateLike(profile, interaction);
          break;
        case "dislike":
          profile = await this.incorporateDislike(profile, interaction);
          break;
        case "purchase":
          profile = await this.incorporatePurchase(profile, interaction);
          break;
        case "search":
          profile = await this.incorporateSearch(profile, interaction);
          break;
        case "pairing_request":
          profile = await this.incorporatePairingRequest(profile, interaction);
          break;
      }

      // Update profile timestamp and save
      profile.lastUpdated = Date.now();
      profile.interactionCount += 1;
      this.preferenceProfiles.set(userId, profile);

      return profile;
    } catch (error) {
      console.error("Error learning from interaction:", error);
      return (
        this.preferenceProfiles.get(userId) || this.createEmptyProfile(userId)
      );
    }
  }

  /**
   * Generate conversation-style beverage recommendations
   * @param {string} conversationHistory - Previous conversation context
   * @param {Array} inventory - Available inventory
   * @param {Object} userProfile - User preference profile
   * @returns {Object} Conversational recommendation response
   */
  async conversationalRecommendation(
    conversationHistory,
    inventory,
    userProfile = {}
  ) {
    try {
      const prompt = this.buildConversationalPrompt(
        conversationHistory,
        inventory,
        userProfile
      );
      const response = await aiServiceIntegration.enhanceBeverage(prompt, {
        type: "conversational_recommendation",
        includeRatings: false,
        includePairings: true,
        customPrompt: true,
      });

      return this.processConversationalResponse(
        response,
        inventory,
        userProfile
      );
    } catch (error) {
      console.error("Error in conversational recommendation:", error);
      return this.fallbackConversationalResponse(inventory);
    }
  }

  /**
   * Discover similar beverages within inventory
   * @param {Object} referenceBeverage - Beverage to find similarities to
   * @param {Array} inventory - Available inventory
   * @param {Object} options - Similarity options
   * @returns {Array} Similar beverages with similarity scores
   */
  async findSimilarBeverages(referenceBeverage, inventory, options = {}) {
    try {
      const similarities = [];

      for (const beverage of inventory) {
        if (beverage.id === referenceBeverage.id) continue;

        const similarity = await this.calculateSimilarity(
          referenceBeverage,
          beverage,
          options
        );
        if (similarity.score > (options.minSimilarity || 0.6)) {
          similarities.push(similarity);
        }
      }

      // Sort by similarity score
      similarities.sort((a, b) => b.score - a.score);

      return similarities.slice(0, options.maxResults || 10);
    } catch (error) {
      console.error("Error finding similar beverages:", error);
      return [];
    }
  }

  /**
   * Generate recommendations for special occasions
   * @param {string} occasion - Type of occasion
   * @param {Array} inventory - Available inventory
   * @param {Object} constraints - Budget, dietary, etc.
   * @returns {Array} Occasion-specific recommendations
   */
  async getOccasionRecommendations(occasion, inventory, constraints = {}) {
    try {
      const prompt = this.buildOccasionPrompt(occasion, inventory, constraints);
      const recommendations = await aiServiceIntegration.enhanceBeverage(
        prompt,
        {
          type: "occasion_recommendation",
          includeRatings: true,
          includePairings: true,
          customPrompt: true,
        }
      );

      return this.processOccasionRecommendations(
        recommendations,
        occasion,
        constraints
      );
    } catch (error) {
      console.error("Error generating occasion recommendations:", error);
      return this.fallbackOccasionRecommendations(occasion, inventory);
    }
  }

  // Private helper methods

  async parseSearchIntent(query, context) {
    try {
      const prompt = `
Parse this natural language beverage search query and extract structured intent:

QUERY: "${query}"
CONTEXT: ${JSON.stringify(context)}

Extract and return JSON with:
1. beverageTypes: ["wine", "beer", "spirits", "cocktails"]
2. flavorProfiles: ["bold", "light", "fruity", "earthy", etc.]
3. priceRange: {min: number, max: number}
4. occasion: string
5. foodPairings: [string]
6. regions: [string]
7. keywords: [string]
8. mood: string
9. searchType: "discovery" | "specific" | "comparison"
10. confidence: number (0-1)

Return structured JSON only.
`;

      const result = await aiServiceIntegration.enhanceBeverage(prompt, {
        type: "search_intent",
        customPrompt: true,
      });

      return typeof result === "string" ? JSON.parse(result) : result;
    } catch (error) {
      console.error("Error parsing search intent:", error);
      return this.fallbackSearchIntent(query);
    }
  }

  async filterByIntent(inventory, intent) {
    return inventory.filter((beverage) => {
      // Type filtering
      if (intent.beverageTypes && intent.beverageTypes.length > 0) {
        if (!intent.beverageTypes.includes(beverage.type?.toLowerCase())) {
          return false;
        }
      }

      // Price range filtering
      if (intent.priceRange) {
        const price = parseFloat(beverage.price?.replace(/[^0-9.]/g, "") || 0);
        if (intent.priceRange.min && price < intent.priceRange.min)
          return false;
        if (intent.priceRange.max && price > intent.priceRange.max)
          return false;
      }

      // Keyword matching
      if (intent.keywords && intent.keywords.length > 0) {
        const searchText = `${beverage.name} ${beverage.description || ""} ${
          beverage.producer || ""
        } ${beverage.region || ""}`.toLowerCase();
        const hasKeyword = intent.keywords.some((keyword) =>
          searchText.includes(keyword.toLowerCase())
        );
        if (!hasKeyword) return false;
      }

      return true;
    });
  }

  async rankSearchResults(candidates, intent, query) {
    try {
      const rankings = [];

      for (const beverage of candidates.slice(0, 50)) {
        // Limit for performance
        const score = await this.calculateSearchScore(beverage, intent, query);
        rankings.push({ beverage, score });
      }

      rankings.sort((a, b) => b.score - a.score);
      return rankings.slice(0, 20); // Return top 20
    } catch (error) {
      console.error("Error ranking search results:", error);
      return candidates
        .slice(0, 20)
        .map((beverage) => ({ beverage, score: 0.5 }));
    }
  }

  async calculateSearchScore(beverage, intent, query) {
    let score = 0.5; // Base score

    // AI enhancement bonus
    if (beverage.aiEnhanced) {
      score += 0.2;
    }

    // Keyword relevance
    if (intent.keywords) {
      const searchText = `${beverage.name} ${
        beverage.description || ""
      }`.toLowerCase();
      const keywordMatches = intent.keywords.filter((keyword) =>
        searchText.includes(keyword.toLowerCase())
      ).length;
      score += (keywordMatches / intent.keywords.length) * 0.3;
    }

    // Flavor profile matching
    if (intent.flavorProfiles && beverage.aiEnhanced?.tastingNotes) {
      const flavorMatches = this.calculateFlavorMatches(
        intent.flavorProfiles,
        beverage.aiEnhanced.tastingNotes
      );
      score += flavorMatches * 0.2;
    }

    // Rating bonus
    if (beverage.aiEnhanced?.ratings?.length > 0) {
      const avgRating =
        beverage.aiEnhanced.ratings.reduce(
          (sum, rating) => sum + rating.score / rating.scale,
          0
        ) / beverage.aiEnhanced.ratings.length;
      score += avgRating * 0.1;
    }

    return Math.min(1.0, score);
  }

  async buildPreferenceProfile(preferences, context) {
    const profile = {
      beverageTypes: preferences.beverageTypes || [],
      flavorPreferences: preferences.flavorPreferences || [],
      priceRanges: preferences.priceRanges || {},
      regions: preferences.regions || [],
      occasions: preferences.occasions || [],
      previousLikes: preferences.previousLikes || [],
      previousDislikes: preferences.previousDislikes || [],
      searchHistory: preferences.searchHistory || [],
      explorationFactor: preferences.explorationFactor || 0.3, // How much to explore vs exploit
      confidence: 0.8,
      context,
    };

    return profile;
  }

  async scoreInventoryAgainstProfile(inventory, profile) {
    const scoredItems = [];

    for (const beverage of inventory) {
      const score = await this.calculateProfileScore(beverage, profile);
      scoredItems.push({ beverage, score });
    }

    return scoredItems.sort((a, b) => b.score - a.score);
  }

  async calculateProfileScore(beverage, profile) {
    let score = 0.5; // Base score

    // Type preference
    if (profile.beverageTypes.includes(beverage.type)) {
      score += 0.2;
    }

    // Previous likes similarity
    if (profile.previousLikes.length > 0) {
      const likesSimilarity = await this.calculateLikesSimilarity(
        beverage,
        profile.previousLikes
      );
      score += likesSimilarity * 0.3;
    }

    // Flavor preferences
    if (
      profile.flavorPreferences.length > 0 &&
      beverage.aiEnhanced?.tastingNotes
    ) {
      const flavorMatch = this.calculateFlavorMatches(
        profile.flavorPreferences,
        beverage.aiEnhanced.tastingNotes
      );
      score += flavorMatch * 0.2;
    }

    // Price range fit
    if (profile.priceRanges.preferred) {
      const price = parseFloat(beverage.price?.replace(/[^0-9.]/g, "") || 0);
      if (
        price >= profile.priceRanges.preferred.min &&
        price <= profile.priceRanges.preferred.max
      ) {
        score += 0.1;
      }
    }

    // Avoid dislikes
    if (profile.previousDislikes.length > 0) {
      const dislikeSimilarity = await this.calculateDislikeSimilarity(
        beverage,
        profile.previousDislikes
      );
      score -= dislikeSimilarity * 0.3;
    }

    return Math.max(0, Math.min(1, score));
  }

  async diversifyRecommendations(scoredItems, profile) {
    const diversified = [];
    const selectedTypes = new Set();
    const explorationTarget = Math.floor(
      scoredItems.length * profile.explorationFactor
    );

    // First, add top-scoring items
    for (const item of scoredItems.slice(0, 10)) {
      if (diversified.length < 5) {
        diversified.push(item);
        selectedTypes.add(item.beverage.type);
      }
    }

    // Then add diverse exploration items
    let explorationCount = 0;
    for (const item of scoredItems.slice(10)) {
      if (explorationCount >= explorationTarget) break;

      if (!selectedTypes.has(item.beverage.type) || Math.random() < 0.3) {
        diversified.push(item);
        selectedTypes.add(item.beverage.type);
        explorationCount++;
      }
    }

    return diversified.slice(0, 12);
  }

  buildConversationalPrompt(conversationHistory, inventory, userProfile) {
    return `
You are an expert sommelier having a conversation with a customer. Based on the conversation history and their profile, recommend beverages from the available inventory.

CONVERSATION HISTORY:
${conversationHistory}

USER PROFILE:
${JSON.stringify(userProfile, null, 2)}

AVAILABLE INVENTORY (sample):
${inventory
  .slice(0, 20)
  .map(
    (item) => `
- ${item.name} (${item.type}) - ${item.price || "Price on request"}
  ${item.description || ""}
  ${item.aiEnhanced ? "AI Enhanced" : ""}
`
  )
  .join("")}

Respond as a knowledgeable but friendly sommelier. Include:
1. 2-3 specific recommendations from inventory
2. Brief explanation for each recommendation
3. Questions to learn more about their preferences
4. Casual, conversational tone

Keep response natural and engaging, under 200 words.
`;
  }

  buildOccasionPrompt(occasion, inventory, constraints) {
    return `
Recommend beverages for this occasion from available inventory:

OCCASION: ${occasion}
CONSTRAINTS: ${JSON.stringify(constraints)}

AVAILABLE INVENTORY:
${inventory
  .slice(0, 30)
  .map(
    (item) => `
- ${item.name} (${item.type}) - ${item.price || "Price on request"}
  ${item.description || ""}
  Enhanced: ${item.aiEnhanced ? "Yes" : "No"}
`
  )
  .join("")}

Provide 6-8 recommendations with:
1. Specific beverage from inventory
2. Why it's perfect for this occasion
3. Serving suggestions
4. Price point appropriateness
5. Alternatives if first choice unavailable

Consider atmosphere, formality, food pairings, and guest expectations.
`;
  }

  // Utility methods

  hashInventory(inventory) {
    return inventory
      .map((item) => item.id || item.name)
      .join(",")
      .substring(0, 100);
  }

  hashPreferences(preferences) {
    return JSON.stringify(preferences).substring(0, 100);
  }

  calculateFlavorMatches(preferences, tastingNotes) {
    if (!preferences.length || !tastingNotes) return 0;

    const allNotes = Object.values(tastingNotes).flat();
    const matches = preferences.filter((pref) =>
      allNotes.some((note) => note.toLowerCase().includes(pref.toLowerCase()))
    );

    return matches.length / preferences.length;
  }

  async calculateLikesSimilarity(beverage, previousLikes) {
    // Simplified similarity calculation
    let similarity = 0;

    for (const like of previousLikes.slice(0, 5)) {
      if (like.type === beverage.type) similarity += 0.3;
      if (like.region === beverage.region) similarity += 0.2;
      if (like.producer === beverage.producer) similarity += 0.4;
    }

    return Math.min(1, similarity / previousLikes.length);
  }

  async calculateDislikeSimilarity(beverage, previousDislikes) {
    // Similar to likes but for dislikes
    let similarity = 0;

    for (const dislike of previousDislikes.slice(0, 5)) {
      if (dislike.type === beverage.type) similarity += 0.2;
      if (dislike.region === beverage.region) similarity += 0.2;
      if (dislike.producer === beverage.producer) similarity += 0.4;
    }

    return Math.min(1, similarity / previousDislikes.length);
  }

  calculateOverallConfidence(recommendations) {
    if (!recommendations.length) return 0;

    const avgConfidence =
      recommendations.reduce((sum, rec) => sum + (rec.confidence || 0.5), 0) /
      recommendations.length;

    return avgConfidence;
  }

  createEmptyProfile(userId) {
    return {
      userId,
      beverageTypes: [],
      flavorPreferences: [],
      priceRanges: {},
      regions: [],
      occasions: [],
      previousLikes: [],
      previousDislikes: [],
      searchHistory: [],
      explorationFactor: 0.3,
      interactionCount: 0,
      createdAt: Date.now(),
      lastUpdated: Date.now(),
    };
  }

  // Processing methods for AI responses

  async explainSearchResults(rankedResults, query, intent) {
    return rankedResults.slice(0, 10).map((result) => ({
      ...result,
      explanation: `Matches your search for "${query}" due to ${this.generateSearchExplanation(
        result.beverage,
        intent
      )}`,
      relevanceScore: result.score,
      timestamp: Date.now(),
    }));
  }

  async explainRecommendations(recommendations, profile, context) {
    return recommendations.map((rec) => ({
      ...rec,
      explanation: this.generateRecommendationExplanation(
        rec.beverage,
        profile
      ),
      confidence: rec.score,
      reasoning: `Based on your preferences for ${profile.flavorPreferences
        .slice(0, 3)
        .join(", ")}`,
      timestamp: Date.now(),
    }));
  }

  generateSearchExplanation(beverage, intent) {
    const reasons = [];

    if (intent.beverageTypes?.includes(beverage.type)) {
      reasons.push(`${beverage.type} category match`);
    }

    if (
      intent.keywords?.some((keyword) =>
        beverage.name.toLowerCase().includes(keyword.toLowerCase())
      )
    ) {
      reasons.push("keyword relevance");
    }

    if (beverage.aiEnhanced) {
      reasons.push("enhanced with professional insights");
    }

    return reasons.join(", ") || "general compatibility";
  }

  generateRecommendationExplanation(beverage, profile) {
    const reasons = [];

    if (profile.beverageTypes.includes(beverage.type)) {
      reasons.push(`matches your preference for ${beverage.type}`);
    }

    if (profile.regions.includes(beverage.region)) {
      reasons.push(`from your preferred ${beverage.region} region`);
    }

    if (beverage.aiEnhanced?.ratings?.length > 0) {
      reasons.push("highly rated by professionals");
    }

    return reasons.join(", ") || "recommended based on your taste profile";
  }

  async calculateSimilarity(beverage1, beverage2, options) {
    let similarity = 0;

    // Type similarity
    if (beverage1.type === beverage2.type) similarity += 0.3;

    // Region similarity
    if (beverage1.region === beverage2.region) similarity += 0.2;

    // Producer similarity
    if (beverage1.producer === beverage2.producer) similarity += 0.2;

    // Flavor profile similarity (if AI enhanced)
    if (
      beverage1.aiEnhanced?.tastingNotes &&
      beverage2.aiEnhanced?.tastingNotes
    ) {
      const flavorSim = this.calculateFlavorProfileSimilarity(
        beverage1.aiEnhanced.tastingNotes,
        beverage2.aiEnhanced.tastingNotes
      );
      similarity += flavorSim * 0.3;
    }

    return {
      beverage: beverage2,
      score: Math.min(1, similarity),
      reasons: this.generateSimilarityReasons(beverage1, beverage2, similarity),
    };
  }

  calculateFlavorProfileSimilarity(notes1, notes2) {
    const allNotes1 = Object.values(notes1).flat();
    const allNotes2 = Object.values(notes2).flat();

    const commonNotes = allNotes1.filter((note) =>
      allNotes2.some((note2) =>
        note2.toLowerCase().includes(note.toLowerCase())
      )
    );

    return commonNotes.length / Math.max(allNotes1.length, allNotes2.length);
  }

  generateSimilarityReasons(beverage1, beverage2, score) {
    const reasons = [];

    if (beverage1.type === beverage2.type) reasons.push("same category");
    if (beverage1.region === beverage2.region) reasons.push("same region");
    if (beverage1.producer === beverage2.producer)
      reasons.push("same producer");
    if (score > 0.8) reasons.push("very similar flavor profiles");

    return reasons;
  }

  // Interaction learning methods

  async incorporateLike(profile, interaction) {
    profile.previousLikes.push({
      beverage: interaction.beverage,
      timestamp: Date.now(),
      context: interaction.context,
    });

    // Update preferences based on liked beverage
    if (!profile.beverageTypes.includes(interaction.beverage.type)) {
      profile.beverageTypes.push(interaction.beverage.type);
    }

    if (
      interaction.beverage.region &&
      !profile.regions.includes(interaction.beverage.region)
    ) {
      profile.regions.push(interaction.beverage.region);
    }

    return profile;
  }

  async incorporateDislike(profile, interaction) {
    profile.previousDislikes.push({
      beverage: interaction.beverage,
      timestamp: Date.now(),
      context: interaction.context,
    });

    return profile;
  }

  async incorporatePurchase(profile, interaction) {
    // Purchases are strong positive signals
    await this.incorporateLike(profile, interaction);

    // Track price range preferences
    const price = parseFloat(
      interaction.beverage.price?.replace(/[^0-9.]/g, "") || 0
    );
    if (price > 0) {
      if (!profile.priceRanges.purchased) profile.priceRanges.purchased = [];
      profile.priceRanges.purchased.push(price);
    }

    return profile;
  }

  async incorporateSearch(profile, interaction) {
    profile.searchHistory.push({
      query: interaction.query,
      timestamp: Date.now(),
      results: interaction.results?.slice(0, 5), // Store top 5 results
    });

    // Keep only recent searches
    profile.searchHistory = profile.searchHistory.slice(-20);

    return profile;
  }

  async incorporatePairingRequest(profile, interaction) {
    if (!profile.pairingHistory) profile.pairingHistory = [];

    profile.pairingHistory.push({
      beverage: interaction.beverage,
      food: interaction.food,
      satisfaction: interaction.satisfaction,
      timestamp: Date.now(),
    });

    return profile;
  }

  // Fallback methods

  fallbackSearchIntent(query) {
    return {
      beverageTypes: [],
      flavorProfiles: [],
      priceRange: null,
      occasion: null,
      foodPairings: [],
      regions: [],
      keywords: query.split(" ").filter((word) => word.length > 2),
      mood: "discovery",
      searchType: "discovery",
      confidence: 0.5,
    };
  }

  fallbackSearch(query, inventory) {
    const keywords = query.toLowerCase().split(" ");
    const results = inventory
      .filter((beverage) =>
        keywords.some(
          (keyword) =>
            beverage.name.toLowerCase().includes(keyword) ||
            beverage.description?.toLowerCase().includes(keyword)
        )
      )
      .slice(0, 10);

    return {
      query,
      intent: this.fallbackSearchIntent(query),
      results: results.map((beverage) => ({
        beverage,
        score: 0.5,
        explanation: "Basic keyword match",
        relevanceScore: 0.5,
        timestamp: Date.now(),
        fallback: true,
      })),
      totalFound: results.length,
      searchTime: Date.now(),
      suggestions: [],
      fallback: true,
    };
  }

  fallbackRecommendations(preferences, inventory) {
    const recommendations = inventory.slice(0, 8).map((beverage, index) => ({
      beverage,
      score: 0.6 - index * 0.05,
      explanation: "General recommendation based on availability",
      confidence: 0.6,
      reasoning: "Fallback recommendation",
      timestamp: Date.now(),
      fallback: true,
    }));

    return {
      profile: this.createEmptyProfile("fallback"),
      recommendations,
      context: {},
      generatedAt: Date.now(),
      confidence: 0.6,
      fallback: true,
    };
  }

  fallbackConversationalResponse(inventory) {
    const sample = inventory.slice(0, 3);
    return {
      response: `I'd be happy to help you find something great! From our selection, I'd recommend checking out ${sample
        .map((item) => item.name)
        .join(", ")}. What kind of flavors are you in the mood for today?`,
      recommendations: sample,
      followUpQuestions: [
        "What's the occasion?",
        "Do you prefer bold or light flavors?",
        "Any specific price range in mind?",
      ],
      timestamp: Date.now(),
      fallback: true,
    };
  }

  fallbackOccasionRecommendations(occasion, inventory) {
    return inventory.slice(0, 6).map((beverage, index) => ({
      beverage,
      occasionFit: 0.7,
      explanation: `Suitable for ${occasion}`,
      servingSuggestions: ["Serve at recommended temperature"],
      priceAppropriate: true,
      alternatives: [],
      confidence: 0.6,
      timestamp: Date.now(),
      fallback: true,
    }));
  }

  async generateSearchSuggestions(query, inventory) {
    // Generate related search suggestions
    const types = [...new Set(inventory.map((item) => item.type))];
    const regions = [
      ...new Set(inventory.map((item) => item.region).filter(Boolean)),
    ];

    return [
      ...types.map((type) => `${type} similar to ${query}`),
      ...regions.slice(0, 3).map((region) => `${region} ${query}`),
      `budget ${query}`,
      `premium ${query}`,
    ].slice(0, 5);
  }

  // Cache management

  clearCaches() {
    this.searchCache.clear();
    this.recommendationCache.clear();
  }

  getCacheStats() {
    return {
      searchCacheSize: this.searchCache.size,
      recommendationCacheSize: this.recommendationCache.size,
      profileCount: this.preferenceProfiles.size,
    };
  }
}

// Export the class for cases where multiple instances are needed
export { AdvancedRecommendationEngine };

// Create and export singleton instance
export const advancedRecommendationEngine = new AdvancedRecommendationEngine();
export default advancedRecommendationEngine;
